import type { PaymentTerms } from '../constants/paymentTerms';
import type { ISODateString, UUID } from './common';
import type { InvoiceLifecycle } from '../utils/invoiceLifecycle';
import type { InvoiceSendState } from '../utils/invoiceSendState';

export const INVOICE_STATUSES = ['needs_cliff_review', 'ready_to_send', 'sent'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  needs_cliff_review: 'Needs Cliff Review',
  ready_to_send: 'Ready to Send',
  sent: 'Sent',
};

export const INVOICE_TYPES = ['cash', 'quickbooks'] as const;
export type InvoiceType = (typeof INVOICE_TYPES)[number];

// === NEW: Invoice document types (for the invoices table) ===

export const INVOICE_KINDS = ['final', 'partial'] as const;
export type InvoiceKind = (typeof INVOICE_KINDS)[number];

export const INVOICE_PAYMENT_STATUSES = ['unpaid', 'paid', 'voided'] as const;
export type InvoicePaymentStatus = (typeof INVOICE_PAYMENT_STATUSES)[number];

export const INVOICE_SOURCES = ['custom', 'lineItems'] as const;
export type InvoiceSource = (typeof INVOICE_SOURCES)[number];

export interface InvoiceLineItem {
  id: UUID;
  invoiceId: UUID;
  category: string; // already display-resolved label
  description: string | null;
  amount: number;
  itemized: boolean;
  sortOrder: number;
  subItems: Array<{
    type: string;
    label: string;
    displayQty: number | null;
    displayRate: number | null;
    displayAmount: number;
    displayUnit: string | null;
    note: string | null;
  }>;
  sourceLineItemId: UUID | null;
  sourceDocumentId: UUID | null;
  sourceTemplateId: string | null;
}

export interface Invoice {
  id: UUID;
  jobId: UUID;
  // Frozen at creation — which breakdown_documents row backs this invoice's
  // PDF. The PDF renders from this doc's current state. Null on legacy
  // pre-migration rows (fall back to the slot's active doc).
  sourceDocumentId: UUID | null;
  invoiceNumber: string;
  kind: InvoiceKind;
  source: InvoiceSource;
  // Cash partials use a separate number sequence (INV-C<n>) and render on the
  // final invoice as a "Cash adjustment" rather than a "Payment".
  isCash: boolean;
  status: InvoicePaymentStatus;
  amount: number;
  // Customer-facing description shown on the PDF.
  description: string | null;
  // Internal-only notes (refund tracking, memos). Never on the PDF.
  notes: string | null;
  invoiceDate: string;
  /**
   * Chosen on the send dialog and printed on the PDF. Null until the first
   * send, which is also why the PDF simply omits the Terms row when it is null
   * — a draft nobody has set terms on should not invent them.
   */
  paymentTerms: PaymentTerms | null;
  /**
   * 'YYYY-MM-DD', or null when the invoice states no terms.
   *
   * Counted server-side from the date the DOCUMENT bears — `sentAt`'s business
   * day once it has one, `invoiceDate` while it is a draft — because that is the
   * date printed on the customer's copy and the date its terms sentence promises
   * payment within. Sent rather than computed in the browser so the job tab and
   * the workspace cannot disagree (spec D6).
   */
  dueDate: string | null;
  // When the invoice was marked paid (actual payment date). Null while
  // unpaid/voided. Distinct from the user-editable invoiceDate.
  paidAt: ISODateString | null;
  pdfGeneratedAt: string | null;
  /** 'external' = synthesised by the W1 backfill from a QuickBooks-era job. */
  origin: 'native' | 'external';
  /** Null = draft. Set by send, by "mark as sent", and by the backfill. */
  sentAt: ISODateString | null;
  voidedAt: ISODateString | null;
  /** Derived server-side from the payment ledger — never recomputed client-side (spec D6). */
  lifecycle: InvoiceLifecycle;
  collected: number;
  outstanding: number;
  /**
   * The NEWEST send attempt's outcome and, on a bad one, the provider's reason
   * (FR-4.6). The job Billing tab renders the same shared row component as the
   * workspace, so the field has to arrive on THIS payload too — the workspace's
   * SQL lateral only feeds /invoices.
   *
   * Optional for the same reason as on WorkspaceInvoiceRow: hand-built Invoice
   * literals in tests and mocks would otherwise all break. The routes always
   * populate it.
   */
  lastSendState?: InvoiceSendState | null;
  lastSendDetail?: string | null;
  createdAt: ISODateString;
  updatedAt: ISODateString;
}

/**
 * What the Edit dialog opens with — the invoice's own content, resolved against
 * the breakdown as it stands now.
 *
 * `lineItemIds` holds ONLY the breakdown lines that still exist, so the picker's
 * checked set is always valid. Snapshot rows whose source line is gone come back
 * in `missingLines` instead, named from the snapshot: the breakdown no longer
 * holds them, so their frozen copy is the only place a name for them survives.
 */
export interface InvoiceContents {
  invoiceId: UUID;
  source: InvoiceSource;
  amount: number;
  description: string | null;
  paymentTerms: PaymentTerms | null;
  isCash: boolean;
  /** Breakdown line ids to pre-check. Always empty for a `custom` invoice. */
  lineItemIds: UUID[];
  missingLines: Array<{ category: string; description: string | null }>;
  /** Null while the invoice is a draft. Decides whether saving supersedes. */
  sentAt: ISODateString | null;
}

export type InvoiceInput = {
  jobId: UUID;
  kind: InvoiceKind;
  amount?: number;
  description?: string;
  invoiceDate?: string;
};

// What regenerating a final invoice would change, without changing it. Drives
// the confirm dialog so the user sees the new total before a billing record is
// overwritten. Returned by GET /api/invoices/:id/regenerate-preview.
export interface RegenerateInvoicePreview {
  invoiceNumber: string;
  /** The invoice's stored cash flag — what the dialog's switch opens on. */
  currentIsCash: boolean;
  /** The flag the preview was asked about (the switch's current position). */
  newIsCash: boolean;
  /**
   * The number the invoice would be recoded to, or null when it keeps the one
   * it has. Non-null exactly when the cash flag is changing: a cash invoice is
   * identified by the C in its number, so the flag and the sequence cannot be
   * allowed to disagree.
   *
   * ADVISORY — peeked without reserving, so a concurrent create can take it
   * first. The confirm step reserves for real; treat this as "the next one in
   * that sequence", not a promise.
   */
  newInvoiceNumber: string | null;
  /**
   * Whether the invoice has already been emailed or marked sent. Recoding is
   * still allowed — the client's copy is wrong either way once the amount
   * changes — but the dialog says so, because someone outside holds a document
   * bearing the old number.
   */
  alreadySent: boolean;
  /**
   * Whether keeping a voided copy would hand the SEND DATE to that copy,
   * leaving the rebuilt invoice as a draft to be sent for the new amount.
   *
   * Reported unconditionally — the preview is not told whether a copy is being
   * kept — so read it alongside the dialog's own switch. False when the invoice
   * was never sent, and false when payments are recorded against it: there the
   * send date stays put, because a draft carrying payments is not a state the
   * lifecycle table has.
   */
  sentDateMovesToCopy: boolean;
  currentAmount: number;
  // Recomputed from the job's currently-active breakdown doc, minus paid partials.
  newAmount: number;
  currentInvoiceDate: string;
  newInvoiceDate: string;
  // True when the job's active breakdown doc has moved on since the invoice was
  // issued — the PDF's line items will change, not just the total.
  sourceDocumentChanged: boolean;
}

export type InvoiceUpdateInput = {
  status?: InvoicePaymentStatus;
  notes?: string | null;
};
